import { useRef, useCallback, useMemo, useEffect } from "react";

/**
 * Hook para observar mudanças de tamanho em um elemento DOM.
 * @param callback Função chamada sempre que o tamanho do elemento muda.
 * @returns Um ref para ser anexado ao elemento que deseja observar.
 */
export const useSizeEffect = <T extends HTMLElement>(callback: (size: { width: number; height: number }) => void): React.RefObject<T | null> => {
	const elementRef = useRef<T>(null);
	const observerRef = useRef<ResizeObserver | null>(null);

	// Guardamos o callback em um ref para evitar que o ResizeObserver
	// seja reiniciado se o usuário passar uma função anônima.
	const callbackRef = useRef(callback);
	callbackRef.current = callback;

	const updateRef = useCallback((element: T | null) => {
		observerRef.current?.unobserve(elementRef.current as Element);
		observerRef.current?.disconnect();
		observerRef.current = null;
		if (!element) return;

		let time: NodeJS.Timeout = undefined as any;

		observerRef.current = new ResizeObserver((entries) => {
			clearTimeout(time);

			time = setTimeout(() => {
				const entry = entries?.[0];
				if (!entry) return;
				const { width, height } = entry.contentRect;
				callbackRef.current({ width, height });
			}, 4);
		});

		observerRef.current.observe(element);
	}, []);

	useEffect(() => {
		updateRef(elementRef.current);

		return () => {
			observerRef.current?.disconnect();
		};
	}, []);

	return useMemo(() => {
		return new Proxy(elementRef, {
			set(target, prop, value) {
				if (prop === "current") {
					updateRef(value);
				}
				(target as any)[prop] = value;
				return true;
			},
		});
	}, []);
};
